/**
 * The ServerTable protocol.
 *
 * These shapes mirror `app/core/list_query.py` **exactly**. They are the wire
 * format, so a change on either side without the other is a filter that
 * silently does nothing — which is why the modes are enumerated here rather
 * than typed as `string`, and why the JSON the client sends is built by
 * {@link toListParams} in one place instead of at each call site.
 */

import type { ReactNode } from 'react';

export type SortDir = 'asc' | 'desc';

export interface SortCriterion {
  field: string;
  dir: SortDir;
}

/** Every string-filter mode the server implements. */
export type StringMode =
  | 'contains'
  | 'not_contains'
  | 'equals'
  | 'not_equals'
  | 'starts'
  | 'not_starts'
  | 'ends'
  | 'not_ends'
  | 'regex'
  | 'blank'
  | 'not_blank';

/** Modes that need no value — the input is hidden for these. */
export const VALUELESS_MODES: readonly StringMode[] = ['blank', 'not_blank'];

export const STRING_MODE_LABELS: Record<StringMode, string> = {
  contains: 'contains',
  not_contains: 'does not contain',
  equals: 'is',
  not_equals: 'is not',
  starts: 'starts with',
  not_starts: 'does not start with',
  ends: 'ends with',
  not_ends: 'does not end with',
  regex: 'matches pattern',
  blank: 'is empty',
  not_blank: 'is not empty',
};

export interface StringCondition {
  mode: StringMode;
  value: string;
}

export type FilterValue =
  | { type: 'string'; conditions: StringCondition[] }
  | { type: 'select'; values: string[] }
  | { type: 'boolean'; value: boolean }
  | { type: 'number-range'; min?: number; max?: number }
  | { type: 'date-range'; from?: string; to?: string };

/** Which filter UI a column gets. */
export type ColumnFilterKind = 'text' | 'select' | 'number' | 'date' | 'boolean';

export interface SelectOption {
  value: string;
  label: string;
}

/** One faceted option with the count the server computed for it. */
export interface FacetOption {
  value: string;
  count: number;
}

export interface ServerColumn<T> {
  /**
   * The backend field name, and the column's identity.
   *
   * Must appear in the endpoint's whitelist, or the column will render and be
   * neither sortable nor filterable — the server drops what it does not
   * recognize rather than erroring, so this is the one thing worth checking
   * against the repository's `LIST_FIELDS`.
   */
  field: string;
  header: string;
  /** A small leading glyph, shown in the header and in the filter popover. */
  icon?: ReactNode;
  sortable?: boolean;
  filter?: ColumnFilterKind;
  /** Options for a `select` filter. Omit to let the server's facets supply them. */
  options?: readonly SelectOption[];
  /** Full control of the cell. Wins over `format`. */
  render?: (row: T) => ReactNode;
  /** Format the raw value for display. */
  format?: (value: unknown, row: T) => ReactNode;
  width?: number;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  /** Right-aligned and tabular. For money and counts, so digits line up. */
  numeric?: boolean;
  /** Hidden by default, still available from the columns panel. */
  hiddenByDefault?: boolean;
  /** Double-click to edit in place. Needs `onCellEdited` on the table. */
  editable?: boolean;
  /** A custom inline editor. Defaults to a text input. */
  editRender?: (context: {
    row: T;
    value: string;
    setValue: (value: string) => void;
    commit: (override?: string) => void;
    cancel: () => void;
  }) => ReactNode;
}

/** What the table sends per change, and what the endpoint accepts. */
export interface ServerListRequest {
  q?: string;
  filters: Record<string, FilterValue>;
  sort: SortCriterion[];
  page: number;
  pageSize: number;
}

/** One page back from the server. */
export interface ServerListResult<T> {
  rows: T[];
  total: number;
}

/**
 * Whether a filter value would actually narrow anything.
 *
 * A filter left in its empty state — an open text condition with no text, a
 * select with nothing ticked — must not be sent. Sent, the server treats it as
 * a real filter, and an empty `select` in particular would match nothing and
 * empty the register for no visible reason.
 */
export function isActiveFilter(value: FilterValue | undefined): boolean {
  if (!value) return false;
  switch (value.type) {
    case 'string':
      return value.conditions.some(
        (condition) =>
          condition.value.trim() !== '' ||
          VALUELESS_MODES.includes(condition.mode),
      );
    case 'select':
      return value.values.length > 0;
    case 'boolean':
      return true;
    case 'number-range':
      return value.min !== undefined || value.max !== undefined;
    case 'date-range':
      return Boolean(value.from) || Boolean(value.to);
  }
}

/** Strip the empty conditions out of a filter before it is sent. */
export function pruneFilter(value: FilterValue): FilterValue | undefined {
  if (value.type === 'string') {
    const conditions = value.conditions.filter(
      (condition) => condition.value.trim() !== '' || VALUELESS_MODES.includes(condition.mode),
    );
    return conditions.length > 0 ? { type: 'string', conditions } : undefined;
  }
  return isActiveFilter(value) ? value : undefined;
}

/**
 * A short human summary of a filter, for its chip.
 *
 * The chip has to say what the filter *does*, not just that one exists.
 * "Status: 2 selected" is actionable; a generic "Filtered" badge means opening
 * every column to find out which.
 */
export function filterChipLabel(value: FilterValue): string {
  switch (value.type) {
    case 'string': {
      const active = value.conditions.filter(
        (condition) =>
          condition.value.trim() !== '' || VALUELESS_MODES.includes(condition.mode),
      );
      if (active.length === 0) return '';
      if (active.length > 1) return `${active.length} conditions`;
      const condition = active[0];
      if (!condition) return '';
      if (VALUELESS_MODES.includes(condition.mode)) return STRING_MODE_LABELS[condition.mode];
      return `${STRING_MODE_LABELS[condition.mode]} "${condition.value}"`;
    }
    case 'select':
      return value.values.length === 1
        ? String(value.values[0])
        : `${value.values.length} selected`;
    case 'boolean':
      return value.value ? 'Yes' : 'No';
    case 'number-range':
      if (value.min !== undefined && value.max !== undefined) return `${value.min}–${value.max}`;
      if (value.min !== undefined) return `≥ ${value.min}`;
      if (value.max !== undefined) return `≤ ${value.max}`;
      return '';
    case 'date-range':
      if (value.from && value.to) return `${value.from} → ${value.to}`;
      if (value.from) return `from ${value.from}`;
      if (value.to) return `until ${value.to}`;
      return '';
  }
}

/**
 * Build the query parameters the endpoint expects.
 *
 * The one place the wire format is written. Empty filters are pruned here, so
 * no call site can send one by leaving a control in its default state.
 */
export function toListParams(
  request: ServerListRequest,
): Record<string, string | number | undefined> {
  const sort = request.sort.map((criterion) => `${criterion.field}:${criterion.dir}`).join(',');

  const filters: Record<string, FilterValue> = {};
  for (const [field, value] of Object.entries(request.filters)) {
    const pruned = pruneFilter(value);
    if (pruned) filters[field] = pruned;
  }

  const hasFilters = Object.keys(filters).length > 0;
  return {
    page: request.page,
    page_size: request.pageSize,
    q: request.q?.trim() || undefined,
    sort: sort || undefined,
    filters: hasFilters ? JSON.stringify(filters) : undefined,
  };
}

/** The empty value for a filter kind, used when a column's filter is opened. */
export function emptyFilterFor(kind: ColumnFilterKind): FilterValue {
  switch (kind) {
    case 'select':
      return { type: 'select', values: [] };
    case 'boolean':
      return { type: 'boolean', value: true };
    case 'number':
      return { type: 'number-range' };
    case 'date':
      return { type: 'date-range' };
    case 'text':
    default:
      return { type: 'string', conditions: [{ mode: 'contains', value: '' }] };
  }
}
